import { useState, useCallback } from 'react';

/**
 * Hook for managing rejection modal state and logic
 * 
 * @param {Function} onReject - Async function to call when rejection is confirmed
 *                              Should accept (id, reason) as parameters
 * @returns {Object} Object containing:
 *   - showRejectDialog: boolean - Whether to show the dialog
 *   - rejectTargetId: string|null - ID of the item being rejected
 *   - openRejectDialog: function - Call with ID to open the dialog
 *   - closeRejectDialog: function - Call to close the dialog
 *   - handleRejectConfirm: function - Call with reason when user confirms
 */
export const useRejectionModal = (onReject) => {
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectTargetId, setRejectTargetId] = useState(null);
  
  const openRejectDialog = useCallback((id) => {
    setRejectTargetId(id);
    setShowRejectDialog(true);
  }, []);
  
  const closeRejectDialog = useCallback(() => {
    setShowRejectDialog(false);
    setRejectTargetId(null);
  }, []);
  
  const handleRejectConfirm = useCallback(async (reason) => {
    if (rejectTargetId && onReject) {
      try {
        await onReject(rejectTargetId, reason);
        closeRejectDialog();
      } catch (error) {
        console.error('[useRejectionModal] Error in onReject:', error);
        // Let the parent handle the error
        throw error;
      }
    }
  }, [rejectTargetId, onReject, closeRejectDialog]);
  
  return {
    // State
    showRejectDialog,
    rejectTargetId,
    
    // Actions
    openRejectDialog,
    closeRejectDialog,
    handleRejectConfirm,
    
    // Convenience props for RejectReasonDialog
    dialogProps: {
      open: showRejectDialog,
      onClose: closeRejectDialog,
      onConfirm: handleRejectConfirm
    }
  };
};